import React, { ReactNode } from 'react';

import { injectIntl, InjectedIntl, FormattedMessage } from 'react-intl';

import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { Field } from '@atlaskit/form';
import Modal, {
  ModalFooter,
  ModalTitle,
  ModalBody,
  ModalHeader,
} from '@atlaskit/modal-dialog';
import TextArea from '@atlaskit/textarea';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { gridSize } from '@atlaskit/theme';

import RefBranch from 'src/components/ref-label/src/refs/ref-branch';
import { FakeLabel, ProcessingMessage } from 'src/styles';
import { getBranchesFullNames } from 'src/utils/compare-branches';

import messages from './branch-dialog.i18n';

export type BranchDialogProps = {
  children?: ReactNode;
  height?: number;
  intl: InjectedIntl;
  isOpen: boolean;
  isLoading: boolean;
  sourceBranchName?: string;
  destinationBranchName?: string;
  sourceRepositoryFullName?: string;
  destinationRepositoryFullName?: string;
  currentPullRequestId?: number;
  title: FormattedMessage.MessageDescriptor;
  actionName: FormattedMessage.MessageDescriptor;
  commitMessage?: string;
  isCommitMessageDisabled?: boolean;
  isCommitMessageDivRemoved?: boolean;
  childrenAboveCommitMsgDiv?: boolean;
  processingMessageTimeoutMs?: number;
  closeSourceBranchEnabled: boolean;
  canDeleteSourceBranch?: boolean;
  onClose: () => void;
  onAction: (payload: object) => void;
};

type State = {
  commitMessage?: string;
  prevPropsCommitMessage?: string;
  showProcessingMessage: boolean;
  closeSourceBranch: boolean;
  currentPullRequestId?: number;
};

class BranchDialog extends React.Component<BranchDialogProps, State> {
  processingMessageTimeout: NodeJS.Timeout;

  // @ts-ignore TODO: fix noImplicitAny error here
  static getDerivedStateFromProps(props, state) {
    if (state.prevPropsCommitMessage === props.commitMessage) {
      return null;
    }
    return {
      commitMessage: props.commitMessage,
      prevPropsCommitMessage: props.commitMessage,
      showProcessingMessage: false,
      currentPullRequestId: props.currentPullRequestId,
    };
  }

  state = {
    commitMessage: undefined,
    showProcessingMessage: false,
    closeSourceBranch: false,
    currentPullRequestId: undefined,
  };

  componentWillUnmount() {
    clearTimeout(this.processingMessageTimeout);
  }

  startShowProcessingMessageTimer = () => {
    const timeoutMs = this.props.processingMessageTimeoutMs
      ? this.props.processingMessageTimeoutMs
      : 5000;
    this.processingMessageTimeout = setTimeout(
      function () {
        this.setState({ showProcessingMessage: true });
      }.bind(this),
      timeoutMs
    );
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  handleMessageChange = e => {
    this.setState({ commitMessage: e.target.value });
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  handleCloseSourceBranchChange = e => {
    this.setState({ closeSourceBranch: e.target.checked });
  };

  getCommitSubmission = () => {
    return this.props.onAction({
      commitMessage: this.state.commitMessage,
      closeBranch: this.state.closeSourceBranch,
      currentPullRequestId: this.state.currentPullRequestId,
    });
  };

  getModalContainer = React.memo(
    ({ children }: { children: React.ReactNode }) => (
      <Form onSubmit={this.getCommitSubmission}>
        {({ formProps }) => <form {...formProps}>{children}</form>}
      </Form>
    )
  );

  getModalFooter = () => (
    <Field name="footer">
      {() => (
        <ModalFooter>
          <div>
            {this.props.closeSourceBranchEnabled && (
              <Checkbox
                isChecked={this.state.closeSourceBranch}
                isDisabled={!this.props.canDeleteSourceBranch}
                label={this.props.intl.formatMessage(
                  messages.closeSourceBranch
                )}
                onChange={this.handleCloseSourceBranchChange}
                name="close-source-branch-checkbox"
                value={this.props.intl.formatMessage(
                  messages.closeSourceBranch
                )}
              />
            )}
          </div>
          <span />
          {this.state.showProcessingMessage && (
            <ProcessingMessage id="processing-message">
              {this.props.intl.formatMessage(messages.processingMessage)}
            </ProcessingMessage>
          )}
          <ButtonGroup>
            <LoadingButton
              type="submit"
              isLoading={this.props.isLoading}
              isDisabled={this.props.isLoading}
              onClick={this.startShowProcessingMessageTimer}
              appearance="primary"
              name={this.props.intl.formatMessage(this.props.actionName)}
            >
              {this.props.intl.formatMessage(this.props.actionName)}
            </LoadingButton>
            <Button
              onClick={this.props.onClose}
              isDisabled={this.props.isLoading}
              name={this.props.intl.formatMessage(messages.closeButton)}
            >
              {this.props.intl.formatMessage(messages.closeButton)}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      )}
    </Field>
  );

  render() {
    const {
      children,
      intl,
      title,
      isOpen,
      isLoading,
      sourceBranchName,
      destinationBranchName,
      sourceRepositoryFullName,
      destinationRepositoryFullName,
      isCommitMessageDisabled,
      isCommitMessageDivRemoved,
      childrenAboveCommitMsgDiv,
      onClose,
      height,
    } = this.props;

    if (!isOpen) {
      return null;
    }

    const fullNames = getBranchesFullNames(
      sourceBranchName,
      destinationBranchName,
      sourceRepositoryFullName,
      destinationRepositoryFullName
    );

    const commitMsgDiv = (
      <Field
        label={intl.formatMessage(messages.commitMessageLabel)}
        name="branch-merge-dialog-commit-message-textfield"
      >
        {({ fieldProps }: any) => (
          <TextArea
            {...fieldProps}
            isDisabled={isCommitMessageDisabled}
            minimumRows={3}
            enableResize
            onChange={this.handleMessageChange}
            value={this.state.commitMessage}
            resize="auto"
          />
        )}
      </Field>
    );

    const BranchModalContainer = this.getModalContainer;

    return (
      <Modal
        onClose={onClose}
        shouldCloseOnEscapePress={!isLoading}
        shouldCloseOnOverlayClick={!isLoading}
        width="medium"
        height={height || gridSize() * 50}
      >
        <ModalHeader>
          <ModalTitle>{intl.formatMessage(title)}</ModalTitle>
        </ModalHeader>
        <BranchModalContainer>
          <ModalBody>
            <FakeLabel>{intl.formatMessage(messages.sourceBranch)}</FakeLabel>
            <RefBranch isFluidWidth name={fullNames.sourceBranchName} />
            <FakeLabel>
              {intl.formatMessage(messages.destinationBranch)}
            </FakeLabel>
            <RefBranch isFluidWidth name={fullNames.destinationBranchName} />

            {childrenAboveCommitMsgDiv && children}
            {!isCommitMessageDivRemoved && commitMsgDiv}
            {!childrenAboveCommitMsgDiv && children}
          </ModalBody>

          {this.getModalFooter()}
        </BranchModalContainer>
      </Modal>
    );
  }
}

export default injectIntl(BranchDialog);
